import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Ticket, CreditCard } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useStableAvatar } from "@/hooks/use-stable-avatar";
import { useCurrencyDetection } from "@/hooks/use-currency-detection";
import { trackBeginCheckout } from "@/utils/analytics";

interface PaymentButtonsProps {
  onPurchase: () => void;
  price?: number;
  isEarlyAccess?: boolean;
  bundleId?: number;
  earlyAccessBookId?: number | null;
  bundleTitle?: string;
  bundleType?: string;
  creditsToUse?: number;
}

export function PaymentButtons({
  onPurchase,
  price = 9.99,
  isEarlyAccess = false,
  bundleId,
  earlyAccessBookId,
  bundleTitle,
  bundleType = 'temporal',
  creditsToUse = 0
}: PaymentButtonsProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { currentProfile } = useStableAvatar();
  const { currency, convertPrice, formatPrice } = useCurrencyDetection();
  
  const handleCheckout = async () => {
    console.log("PaymentButtons handleCheckout called with:", { 
      bundleId, 
      price, 
      isEarlyAccess, 
      earlyAccessBookId, 
      creditsToUse,
      currentProfile: currentProfile?.name
    });
    
    // Check if current profile is a kids profile and block purchase
    if (currentProfile && 'profile_type' in currentProfile && currentProfile.profile_type === 'kids') {
      console.log("Kids profile detected - blocking purchase");
      toast({
        title: "Purchase Not Allowed",
        description: "Kids profiles cannot make purchases. Please switch to an adult profile or ask a parent for help.",
        variant: "destructive"
      });
      return;
    }
    
    if (!bundleId) {
      console.error("Missing bundleId in PaymentButtons:", { 
        bundleId, 
        receivedProps: { bundleId, price, isEarlyAccess, earlyAccessBookId, creditsToUse }
      });
      toast({
        title: "Error",
        description: "Bundle ID is missing. Please try again.",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    
    try {
      console.log("Starting checkout process for bundle:", bundleId, {
        isEarlyAccess,
        earlyAccessBookId,
        price,
        bundleType,
        creditsToUse,
        currentProfile: currentProfile?.name
      });
      
      // Get current user session
      const { data: sessionData } = await supabase.auth.getSession();
      
      if (!sessionData.session) {
        toast({
          title: "Please log in",
          description: "You need to be logged in to make a purchase.",
          variant: "destructive"
        });
        setIsLoading(false);
        return;
      }
      
      console.log("User authenticated, proceeding with checkout");
      
      // Track begin checkout event
      trackBeginCheckout({
        value: price,
        currency: currency.code,
        items: [{
          item_id: bundleId ? `bundle_${bundleId}` : `book_${earlyAccessBookId}`,
          item_name: bundleId ? 'Bundle Purchase' : 'Book Purchase',
          item_category: isEarlyAccess ? 'Early Access' : 'Bundle',
          price: price,
          quantity: 1
        }]
      });
      
      // Call the create-checkout edge function with currency information (no conversion)
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { 
          bundleId,
          isEarlyAccess,
          earlyAccessBookId: earlyAccessBookId || undefined,
          clientPrice: price,
          creditsToUse,
          currency: currency.code
        }
      });
      
      if (error) {
        console.error("Checkout function error:", error);
        throw error;
      }
      
      console.log("Checkout session created:", data);
      
      // Execute the onPurchase callback
      onPurchase();
      
      // Redirect to Stripe checkout
      if (data?.url) {
        console.log("Redirecting to Stripe:", data.url);
        window.location.href = data.url;
      } else {
        console.error("No checkout URL returned:", data);
        throw new Error('No checkout URL returned');
      }
      
    } catch (error) {
      console.error('Checkout error:', error);
      
      // Handle specific error cases
      const errorMessage = error instanceof Error ? error.message : "Failed to initialize checkout";
      
      if (errorMessage.includes('amount_too_small') || errorMessage.includes('Payment amount too small')) {
        toast({
          title: "Amount Too Small",
          description: "The payment amount is below the minimum required. Please contact support.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Checkout Error",
          description: errorMessage,
          variant: "destructive"
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Final price after credits (same amount, no conversion)
  const finalPriceUSD = Math.max(price - creditsToUse, 0);
  const finalPrice = finalPriceUSD; // No conversion needed

  // Dynamic button text based on bundle type
  const getButtonText = () => {
    if (isLoading) return "Processing...";
    
    if (currentProfile && 'profile_type' in currentProfile && currentProfile.profile_type === 'kids') {
      return "Ask Parent to Purchase";
    }
    
    if (bundleType === 'permanent') {
      return `Unlock ${bundleTitle || 'Bundle'} - ${formatPrice(finalPriceUSD)}`;
    }
    
    if (isEarlyAccess) {
      return `Pre-Order - ${formatPrice(finalPriceUSD)}`;
    }
    
    return `Proceed to Checkout - ${formatPrice(finalPriceUSD)}`;
  };

  const isKidsProfile = currentProfile && 'profile_type' in currentProfile && currentProfile.profile_type === 'kids';

  return (
    <div className="space-y-2">
      <button 
        onClick={handleCheckout} 
        className={`w-full flex items-center justify-center gap-2 text-white py-2 md:py-3 px-4 md:px-6 h-auto
                   backdrop-blur-md border-2 rounded-lg transition-all duration-300 ease-in-out cursor-pointer
                   ${isKidsProfile 
                     ? "bg-gray-500/30 border-gray-500 hover:bg-gray-600/30" 
                     : "bg-black/30 border-purple-500 shadow-[0_0_20px_rgba(147,51,234,0.6)]"
                   }`}
        style={!isKidsProfile ? {
          boxShadow: '0 0 30px rgba(147, 51, 234, 0.8), inset 0 0 30px rgba(147, 51, 234, 0.1)',
        } : undefined}
        disabled={isLoading || isKidsProfile}
      >
        <div className="relative z-10 flex items-center justify-center gap-2">
          <CreditCard className="h-4 w-4" />
          {getButtonText()}
        </div>
      </button>
      
      {isKidsProfile && (
        <p className="text-xs text-center text-white/60 mt-2">
          Switch to an adult profile to make purchases
        </p>
      )}
    </div>
  );
}
